import { useState } from 'react'
import { useFinance } from '../context/FinanceContext'
import { useSettings } from '../context/SettingsContext'
import { getThemeColors } from '../lib/constants'
import { trackCalculatorUse } from '../lib/analytics'
import PageShell from '../components/layout/PageShell'
import PaycheckCalculator from '../components/scenarios/PaycheckCalculator'
import BonusRsuTax from '../components/scenarios/BonusRsuTax'
import SelfEmploymentTax from '../components/scenarios/SelfEmploymentTax'
import FreelancerRate from '../components/scenarios/FreelancerRate'
import LifestyleCreep from '../components/scenarios/LifestyleCreep'

const TABS = [
  { key: 'paycheck', label: 'Paycheck', icon: PaycheckIcon },
  { key: 'bonusrsu', label: 'Bonus / RSU', icon: GiftIcon },
  { key: 'selfemployment', label: 'Self-Employment', icon: BriefcaseIcon },
  { key: 'freelancer', label: 'Freelancer Rate', icon: ClipboardIcon },
  { key: 'lifestylecreep', label: 'Lifestyle Creep', icon: TrendUpIcon },
]

export default function IncomeCareer() {
  const { state } = useFinance()
  const { trackCalculatorOpen } = useSettings()
  const colors = getThemeColors(state.theme)
  const validTabs = TABS.map(t => t.key)
  const [activeTab, setActiveTab] = useState(validTabs.includes(state.activeTab) ? state.activeTab : 'paycheck')

  return (
    <PageShell
      title="Income & Career"
      subtitle="Paycheck calculator, bonus/RSU taxes, self-employment, freelancer rates, and lifestyle creep analysis"
    >
      <div className="flex gap-1 p-1 rounded-2xl glass mb-6 max-w-fit overflow-x-auto">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => { setActiveTab(key); trackCalculatorUse(key); trackCalculatorOpen(key) }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 whitespace-nowrap ${
              activeTab === key
                ? 'text-heading shadow-sm'
                : 'text-muted hover:text-heading'
            }`}
            style={activeTab === key ? {
              background: `linear-gradient(135deg, ${colors.primary}22, ${colors.secondary}22)`,
              border: `1px solid ${colors.primary}33`,
            } : {}}
          >
            <Icon active={activeTab === key} />
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      <div className="animate-fade-in">
        {activeTab === 'paycheck' && <PaycheckCalculator />}
        {activeTab === 'bonusrsu' && <BonusRsuTax />}
        {activeTab === 'selfemployment' && <SelfEmploymentTax />}
        {activeTab === 'freelancer' && <FreelancerRate />}
        {activeTab === 'lifestylecreep' && <LifestyleCreep />}
      </div>
    </PageShell>
  )
}

function PaycheckIcon({ active }) {
  return (
    <svg className="w-4 h-4" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function GiftIcon({ active }) {
  return (
    <svg className="w-4 h-4" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
    </svg>
  )
}

function BriefcaseIcon({ active }) {
  return (
    <svg className="w-4 h-4" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0" />
    </svg>
  )
}

function ClipboardIcon({ active }) {
  return (
    <svg className="w-4 h-4" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25" />
    </svg>
  )
}

function TrendUpIcon({ active }) {
  return (
    <svg className="w-4 h-4" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
    </svg>
  )
}
